import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { creditos, gramos, sufijoUnidad } from "../lib/formato";
import { Aviso, Cargando, Panel, Vacio } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";
import { useSesionPortal } from "./sesionPortal";

interface ProductoPortal {
  id: string;
  nombre: string;
  genetica: string;
  thc: number | null;
  cbd: number | null;
  categoria: {
    id: string;
    nombre: string;
    color: string;
    unidadMedida: string;
  };
  hayStock: boolean;
  precioCentimos: number;
  precioBaseCentimos: number;
  descuentoTexto: string | null;
}

/**
 * Lo que hay disponible, con el precio que le corresponde a este socio, y la
 * posibilidad de reservarlo para pasar a recogerlo.
 */
export default function PortalCatalogo({
  alReservar,
}: {
  alReservar?: () => void;
}) {
  const cliente = useQueryClient();
  const { datos, refrescar } = useSesionPortal();
  const { t, delFallo } = useIdioma();
  const saldoCentimos = datos?.socio.saldoCentimos ?? 0;
  const catalogo = useQuery({
    queryKey: ["portal-catalogo"],
    queryFn: () => api<ProductoPortal[]>("/portal/catalogo"),
  });

  /** Lo que el socio va marcando, en gramos por producto. */
  const [pedido, setPedido] = useState<Record<string, number>>({});
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  const totalG = Object.values(pedido).reduce((s, g) => s + g, 0);

  // Lo que costaría, con los precios que ya vienen calculados para este socio.
  // Es orientativo: el precio bueno lo pone el club al guardar la reserva.
  const totalCentimos = Object.entries(pedido).reduce((suma, [id, g]) => {
    const producto = (catalogo.data ?? []).find((p) => p.id === id);
    return suma + Math.round((producto?.precioCentimos ?? 0) * g);
  }, 0);

  const faltaSaldo = totalCentimos > saldoCentimos;

  const sumar = (id: string, gramosMas: number) =>
    setPedido((p) => {
      const nuevo = Math.max(
        0,
        Math.round(((p[id] ?? 0) + gramosMas) * 10) / 10,
      );
      const copia = { ...p };
      if (nuevo === 0) delete copia[id];
      else copia[id] = nuevo;
      return copia;
    });

  async function reservar() {
    setError("");
    setEnviando(true);
    try {
      await api("/reservas/mias", {
        metodo: "POST",
        cuerpo: {
          lineas: Object.entries(pedido).map(([productoId, cantidadG]) => ({
            productoId,
            cantidadG,
          })),
        },
      });
      setPedido({});
      void cliente.invalidateQueries({ queryKey: ["portal-reservas"] });
      // El saldo no cambia al reservar, pero si lo hace la reserva abierta.
      void refrescar();
      alReservar?.();
    } catch (fallo) {
      setError(delFallo(fallo, "catalogo.falloReserva"));
    } finally {
      setEnviando(false);
    }
  }

  if (catalogo.isLoading || !catalogo.data)
    return <Cargando texto={t("general.cargando")} />;
  if (catalogo.data.length === 0) return <Vacio>{t("catalogo.vacio")}</Vacio>;

  const porCategoria = new Map<string, ProductoPortal[]>();
  for (const p of catalogo.data) {
    const lista = porCategoria.get(p.categoria.nombre) ?? [];
    lista.push(p);
    porCategoria.set(p.categoria.nombre, lista);
  }

  const genetica = (codigo: string) => t(`genetica.${codigo}` as Clave);

  return (
    <div className="columna" style={{ gap: 16 }}>
      <p className="suave pequeno" style={{ margin: 0 }}>
        {t("catalogo.condiciones")}
      </p>

      <Aviso tipo="error">{error}</Aviso>

      {saldoCentimos <= 0 && (
        <Aviso tipo="atencion">{t("catalogo.sinSaldo")}</Aviso>
      )}

      {[...porCategoria.entries()].map(([categoria, productos]) => (
        <Panel key={categoria} titulo={categoria}>
          <div className="portal-productos">
            {productos.map((p) => (
              <div
                key={p.id}
                className="portal-producto"
                style={p.hayStock ? undefined : { opacity: 0.5 }}
              >
                <div
                  className="cinta"
                  style={{ background: p.categoria.color }}
                />
                <strong>{p.nombre}</strong>
                <div className="suave pequeno">
                  {["SATIVA", "INDICA", "HIBRIDA", "CBD", "NA"].includes(
                    p.genetica,
                  )
                    ? genetica(p.genetica)
                    : p.genetica}
                  {p.thc ? ` · THC ${p.thc}%` : ""}
                  {p.cbd ? ` · CBD ${p.cbd}%` : ""}
                </div>
                <div className="fila separada" style={{ marginTop: 6 }}>
                  <strong className="tabular">
                    {p.descuentoTexto && (
                      <span
                        className="suave"
                        style={{
                          textDecoration: "line-through",
                          fontWeight: 400,
                          marginRight: 5,
                        }}
                      >
                        {creditos(p.precioBaseCentimos)}
                      </span>
                    )}
                    {creditos(p.precioCentimos)}/
                    {sufijoUnidad(p.categoria.unidadMedida)}
                  </strong>
                  {!p.hayStock && (
                    <span className="etiqueta roja">
                      {t("catalogo.sinStock")}
                    </span>
                  )}
                </div>
                {p.descuentoTexto && (
                  <span
                    className="etiqueta verde"
                    style={{ alignSelf: "flex-start", marginTop: 4 }}
                  >
                    {p.descuentoTexto}
                  </span>
                )}
                {p.hayStock && (
                  <div className="fila separada" style={{ marginTop: 8 }}>
                    <div className="fila" style={{ gap: 6 }}>
                      <button
                        className="boton secundario"
                        onClick={() => sumar(p.id, -1)}
                        aria-label={t("catalogo.quitarGramo")}
                      >
                        −
                      </button>
                      <span
                        className="tabular"
                        style={{ minWidth: 44, textAlign: "center" }}
                      >
                        {pedido[p.id] ? `${pedido[p.id]} g` : "—"}
                      </span>
                      <button
                        className="boton secundario"
                        onClick={() => sumar(p.id, 1)}
                        aria-label={t("catalogo.anadirGramo")}
                      >
                        +
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      ))}

      {totalG > 0 && (
        <div className="barra-reserva">
          <div>
            <strong className="tabular">
              {gramos(totalG * 1000)} · {creditos(totalCentimos)}
            </strong>
            <div className="pequeno suave">
              {faltaSaldo ? (
                <span style={{ color: "var(--rojo)" }}>
                  {t("catalogo.faltan", {
                    importe: creditos(totalCentimos - saldoCentimos),
                  })}
                </span>
              ) : (
                t("catalogo.quedarian", {
                  importe: creditos(saldoCentimos - totalCentimos),
                })
              )}
            </div>
          </div>
          <div className="fila" style={{ gap: 8 }}>
            <button className="boton secundario" onClick={() => setPedido({})}>
              {t("catalogo.vaciar")}
            </button>
            <button
              className="boton"
              onClick={() => void reservar()}
              disabled={enviando || faltaSaldo}
            >
              {enviando ? t("catalogo.reservando") : t("catalogo.reservar")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
